// ---------------------------------------------------------------------------
// Telas guardadas do frame (BUG-331, 06/10/2026) — a regra pura.
//
// Até aqui toda navegação DESMONTAVA a tela de onde se saiu, e o voltar a
// montava do zero: skeleton no que não tem memória de sessão, capas
// decodificando de novo, rolagem que só alcança a posição quando o dado chega
// — e, no iPhone, o WebKit tirando a foto do gesto de voltar justamente desse
// estado. Medido em harness (build de produção, CPU 6×): 0/32 voltas com o 1º
// quadro na posição certa; com a tela guardada, 32/32 e 0 quadro de skeleton.
//
// Quem guarda é o `KeepAliveOutlet` (com `<Activity>` do React); aqui mora só a
// decisão de QUAL instância cada navegação usa, para ser testável sem DOM:
//
//  · uma instância por PATHNAME — folha com histórico empilha entrada de mesmo
//    endereço e a Busca reescreve `?q=`: as duas ficam na MESMA instância;
//  · só o voltar (POP) reaproveita uma tela escondida. Abrir uma tela por link
//    (PUSH/REPLACE) monta uma nova, como sempre foi — quem toca em "Ajustes"
//    espera Ajustes do começo, não o painel que ficou aberto da última vez;
//  · teto de telas guardadas; sai a usada há mais tempo, nunca a da tela.
// ---------------------------------------------------------------------------

import type { Location, NavigationType } from "react-router-dom";

/** Quantas telas ficam guardadas (a da tela incluída). */
export const KEPT_PAGES_LIMIT = 5;

export interface KeptPage {
  /** Identidade da instância — vira a `key` do React. Nova = tela montada do zero. */
  id: number;
  path: string;
  /** A location que a instância enxerga (congelada enquanto escondida). */
  location: Location;
  /** Relógio de uso, para o despejo. */
  used: number;
}

export interface KeptPagesState {
  pages: KeptPage[];
  activeId: number | null;
  nextId: number;
}

export const EMPTY_KEPT_PAGES: KeptPagesState = { pages: [], activeId: null, nextId: 1 };

/**
 * Mesma entrada do histórico, mesmo `state` e mesma busca: devolve o objeto
 * antigo, para o `memo` da tela segurar o re-render (a location do POP é um
 * objeto novo para a mesma entrada).
 */
function sameLocation(a: Location, b: Location): boolean {
  return a.key === b.key && a.state === b.state && a.search === b.search && a.hash === b.hash;
}

export function nextKeptPages(
  prev: KeptPagesState,
  location: Location,
  navigationType: NavigationType,
  limit: number = KEPT_PAGES_LIMIT,
): KeptPagesState {
  const clock = prev.pages.reduce((max, p) => Math.max(max, p.used), 0) + 1;
  const existing = prev.pages.find((p) => p.path === location.pathname);
  const reuse =
    existing !== undefined && (existing.id === prev.activeId || navigationType === "POP");

  let nextId = prev.nextId;
  let active: KeptPage;
  let pages: KeptPage[];
  if (reuse) {
    active = {
      ...existing,
      location: sameLocation(existing.location, location) ? existing.location : location,
      used: clock,
    };
    pages = prev.pages.map((p) => (p.id === existing.id ? active : p));
  } else {
    active = { id: nextId++, path: location.pathname, location, used: clock };
    pages = [...prev.pages.filter((p) => p.path !== location.pathname), active];
  }

  // A da tela acabou de receber o relógio mais novo: a mais antiga nunca é ela.
  while (pages.length > Math.max(1, limit)) {
    const oldest = pages.reduce((a, b) => (b.used < a.used ? b : a));
    pages = pages.filter((p) => p.id !== oldest.id);
  }

  // Nada mudou (re-render sem navegação): devolve o MESMO objeto.
  if (
    reuse &&
    active.location === existing.location &&
    prev.activeId === active.id &&
    pages.length === prev.pages.length
  ) {
    return prev;
  }
  return { pages, activeId: active.id, nextId };
}
